import { object, mixed, string, number, array } from 'yup'
import { windDirectionAssignments } from '../OuterWallAssignment/constants'
import { windDirections } from '../RoofAssignment/constants'

const forceSchema = object({
  x: number().default(0),
  y: number().default(0),
  z: number().default(0),
})

export const createOuterWallAssignmentSchema = (message: string) =>
  array().of(string()).required().min(1, message).default([])

const createWindloadCeilingSchema = (slabGuid: string) =>
  object({
    slab_guid: string().default(slabGuid),
    force: forceSchema,
    point_of_attack: forceSchema,
    comment: string().default(''),
  })

const windloadRoofSchema = object({
  force: forceSchema,
  point_of_attack: forceSchema,
  comment: string().default(''),
})

export const roofDirectionAssignmentSchema = object({
  shapes: array().default([]),
  configurations: array().min(1, 'Bitte weisen sie mindestens eine Dachfläche zu').default([]),
})

const pointSchema = object({
  x: number().required().default(0),
  y: number().required().default(0),
  z: number().required().default(0),
})

const buildingDimensionsWithStartSchema = object({
  height: number().required().moreThan(0).default(10),
  depth: number().required().moreThan(0).default(10),
  width: number().required().moreThan(0).default(10),
  start: pointSchema.required().default(() => pointSchema.getDefault()),
})

const windBoxSideConfigSchema = object({
  length: number().required().moreThan(0).default(10),
  section_value: number().required().moreThan(0).default(10),
  start: pointSchema.required().default(() => pointSchema.getDefault()),
  direction: pointSchema.required().default(() => pointSchema.getDefault()),
})

const windBoxSchema = object({
  ABC_left: windBoxSideConfigSchema.required().default(() => windBoxSideConfigSchema.getDefault()),
  ABC_right: windBoxSideConfigSchema.required().default(() => windBoxSideConfigSchema.getDefault()),
  D: windBoxSideConfigSchema.required().default(() => windBoxSideConfigSchema.getDefault()),
  E: windBoxSideConfigSchema.required().default(() => windBoxSideConfigSchema.getDefault()),
})

const windBoxConfigSchema = object({
  Direction0: windBoxSchema.required().default(() => windBoxSchema.getDefault()),
  Direction90: windBoxSchema.required().default(() => windBoxSchema.getDefault()),
  Direction180: windBoxSchema.required().default(() => windBoxSchema.getDefault()),
  Direction270: windBoxSchema.required().default(() => windBoxSchema.getDefault()),
})

const simplifiedMethodStandortValues = [
  'Binnenland',
  'KuesteOstseeInseln',
  'KuesteNordOstseeInselnOstsee',
  'InselnNordsee',
] as const

export const createWindLoadsSchema = () => {
  return object({
    roofBuildingDimensions: buildingDimensionsWithStartSchema.required(),
    qZoneBuildingDimensions: buildingDimensionsWithStartSchema.when(
      'baseParameters.computationMethod',
      {
        is: 'GroundRoughnessConsidered',
        then: schema => schema.required(),
        otherwise: schema => schema.notRequired(),
      },
    ),
    wallBuildingDimensions: buildingDimensionsWithStartSchema.required(),
    windBoxConfig: windBoxConfigSchema.required(),
    baseParameters: object({
      wind_zone: mixed().oneOf([1, 2, 3, 4]).default(1),
      wind_area_type: string().nullable().default('Inland'),
      computationMethod: mixed()
        .oneOf(['GroundRoughnessConsidered', 'SimplifiedMethod'])
        .default('SimplifiedMethod'),
      wind_speed: number().when('computationMethod', {
        is: 'GroundRoughnessConsidered',
        then: schema => schema.required().moreThan(0),
        otherwise: schema => schema.notRequired(),
      }),
      baseWindPressure_q_b: number().when('computationMethod', {
        is: 'GroundRoughnessConsidered',
        then: schema => schema.required().moreThan(0),
        otherwise: schema => schema.notRequired(),
      }),
      terrain_category: mixed().when('computationMethod', {
        is: 'GroundRoughnessConsidered',
        then: schema => schema.oneOf(['I', 'II', 'III', 'IV']).required(),
        otherwise: schema => schema.notRequired(),
      }),
      gustPressure_q_p: number().when('computationMethod', {
        is: 'SimplifiedMethod',
        then: schema => schema.required().moreThan(0),
        otherwise: schema => schema.notRequired(),
      }),
      buildingHeightFor_q_p_lookup: number().when('computationMethod', {
        is: 'SimplifiedMethod',
        then: schema => schema.required().moreThan(0),
        otherwise: schema => schema.notRequired(),
      }),
      simplifiedMethodStandort: string().when('computationMethod', {
        is: 'SimplifiedMethod',
        then: schema => schema.oneOf([...simplifiedMethodStandortValues]).required(),
        otherwise: schema => schema.notRequired(),
      }),
    }),
    wallAssignment: object({
      ...windDirectionAssignments.reduce<{
        [key: string]: any // eslint-disable-line
      }>((acc, { value, label }) => {
        acc[value] = createOuterWallAssignmentSchema(
          `Bitte weisen Sie ${label} mindestens eine Außenwand zu`,
        )

        return acc
      }, {}),
      wind_direction: string().default('Direction0'),
    }),
    roofAssignment: object({
      ...windDirections.reduce((acc, direction) => {
        acc = {
          ...acc,
          [direction]: roofDirectionAssignmentSchema,
        }

        return acc
      }, {}),
    }),
  })
}

export const createManualWindLoadsSchema = (slabs: string[]) => {
  return object({
    results: object({
      wind_loads_per_ceiling: object({
        ...windDirections.reduce((acc, direction) => {
          acc = {
            ...acc,
            [direction]: array()
              .of(createWindloadCeilingSchema('guid'))
              .default(slabs.map(slab => createWindloadCeilingSchema(slab).getDefault())),
          }

          return acc
        }, {}),
      }),
      wind_loads_roof: object({
        ...windDirections.reduce((acc, direction) => {
          acc = {
            ...acc,
            [direction]: array().of(windloadRoofSchema).default([windloadRoofSchema.getDefault()]),
          }

          return acc
        }, {}),
      }),
    }),
  })
}
